import { useLayoutEffect, useRef, useState } from "react";
import { classMerge } from "../utility/classMerge";
import { useScaffold } from "./Scaffold";
import styles from "./Scaffold.module.css";
import { ScaffoldCell } from "./ScaffoldCell";
import { ScaffoldRowResizeHandle } from "./ScaffoldRowResizeHandle";
import { ScaffoldRowSelectHandle } from "./ScaffoldRowSelectHandle";
import { ROW_ENTER_COMPLETE, ROW_LEAVE_COMPLETE } from "./context/interface";
import {
  SCAFFOLD_ANIMATION_DURATION_MS,
  SCAFFOLD_ANIMATION_START_DELAY_MS,
  ScaffoldAnimationState,
} from "./interface";

export interface ScaffoldRowProps {
  rowId: string;
}

/**
 * Row component for the Scaffold, rendering a single row with cells based on the provided column definitions.
 * Handles both entering rows (height 0 → full) and leaving rows (full → 0) using a state machine pattern.
 * Uses overflowY: hidden to control overflow during height transitions.
 *
 * @param props - Component props
 * @param props.rowId - Unique identifier for the row
 * @returns A React element representing a scaffold row with cells, or null if row is closed.
 * @throws Will throw an error if ScaffoldRow is used outside of a ScaffoldContext provider.
 */
export const ScaffoldRow = ({ rowId }: ScaffoldRowProps) => {
  const scaffoldContext = useScaffold();
  const { cols, rows } = scaffoldContext.settings;
  const { leavingCols, enteringRowIds, leavingRows } = scaffoldContext.state;

  const isEnteringRow = enteringRowIds.has(rowId);
  const leavingRow = leavingRows.find((r) => r.id === rowId);
  const row = rows.find((r) => r.id === rowId) || leavingRow;

  /**
   * Determine initial state: entering rows start at "placed" (0px), leaving start at "open" (full)
   */
  const getInitialState = (): ScaffoldAnimationState => (isEnteringRow ? "placed" : "open");

  const [state, setState] = useState<ScaffoldAnimationState>(getInitialState());

  // Store timeout IDs to clear them on cleanup
  const timeoutsRef = useRef<number[]>([]);
  const wasLeavingRowRef = useRef(Boolean(leavingRow));
  const hasNotifiedRowLeaveCompleteRef = useRef<boolean>(false);
  const hasNotifiedRowEnterCompleteRef = useRef<boolean>(false);

  /**
   * State machine for entering rows: placed (5ms delay) → opening (animate for 200ms) → open
   * State machine for leaving rows: open → closing (animate for 200ms) → closed (unmount)
   */
  useLayoutEffect(() => {
    if (state !== "closing") {
      hasNotifiedRowLeaveCompleteRef.current = false;
    }

    if (state !== "opening") {
      hasNotifiedRowEnterCompleteRef.current = false;
    }

    if (state === "placed") {
      const id = window.setTimeout(() => {
        setState("opening");
      }, SCAFFOLD_ANIMATION_START_DELAY_MS);
      timeoutsRef.current.push(id);
    }
    if (state === "opening") {
      const id = window.setTimeout(() => {
        setState("open");
        /* istanbul ignore else */
        if (isEnteringRow && !hasNotifiedRowEnterCompleteRef.current) {
          hasNotifiedRowEnterCompleteRef.current = true;
          scaffoldContext.dispatch({ operation: ROW_ENTER_COMPLETE, rowId });
        }
      }, SCAFFOLD_ANIMATION_DURATION_MS);
      timeoutsRef.current.push(id);
    }
    if (state === "closing") {
      const id = window.setTimeout(() => {
        setState("closed");
        /* istanbul ignore else */
        if (leavingRow && !hasNotifiedRowLeaveCompleteRef.current) {
          hasNotifiedRowLeaveCompleteRef.current = true;
          scaffoldContext.dispatch({ operation: ROW_LEAVE_COMPLETE, rowId });
        }
      }, SCAFFOLD_ANIMATION_DURATION_MS);
      timeoutsRef.current.push(id);
    }

    // Cleanup: clear all scheduled timeouts
    return () => {
      timeoutsRef.current.forEach((id) => clearTimeout(id));
      timeoutsRef.current = [];
    };
  }, [isEnteringRow, leavingRow, rowId, scaffoldContext, state]);

  /**
   * When a row enters the leaving state, trigger the closing animation.
   */
  useLayoutEffect(() => {
    if (leavingRow && state === "open") {
      // This needs to be disabled as immediate effects need to occur from useLayoutEffect
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState("closing");
    }
  }, [leavingRow, state]);

  /**
   * If a row stops being a leaving row before the close animation completes,
   * reset its state so it can remain visible.
   */
  useLayoutEffect(() => {
    const wasLeavingRow = wasLeavingRowRef.current;
    const isLeavingRow = Boolean(leavingRow);

    if (wasLeavingRow && !isLeavingRow) {
      // This needs to be disabled as immediate effects need to occur from useLayoutEffect
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState(isEnteringRow ? "placed" : "open");
    }

    wasLeavingRowRef.current = isLeavingRow;
  }, [isEnteringRow, leavingRow]);

  const currentColIdSet = new Set(cols.map((c) => c.id));
  const allDisplayCols = [...cols, ...leavingCols.filter((c) => !currentColIdSet.has(c.id))].sort(
    (a, b) => {
      if (a.colNumber !== b.colNumber) {
        return a.colNumber - b.colNumber;
      }
      // When col numbers collide during removal, keep the leaving col first so it visibly collapses.
      const isALeavingCol = !currentColIdSet.has(a.id);
      const isBLeavingCol = !currentColIdSet.has(b.id);
      if (isALeavingCol === isBLeavingCol) return 0;
      return isALeavingCol ? -1 : 1;
    },
  );

  const rowHeight =
    row?.height ??
    // istanbul ignore next
    0;
  const previewRowHeight = scaffoldContext.state.previewRowHeights[rowId];
  const liveRowHeight = previewRowHeight ?? rowHeight;
  // Height is 0 in "placed" state (entering), or when "closing" (leaving). Otherwise full height.
  const displayHeight = state === "placed" || state === "closing" ? 0 : liveRowHeight;

  /** True when any cell in this row has an active rowSpan; requires overflow:visible on rowCells. */
  const rowHasRowSpanningCells = allDisplayCols.some(
    (c) => scaffoldContext.headerSpanMap?.[rowId]?.[c.id]?.hasRowSpan,
  );

  return state === "closed" ? null : (
    <div
      className={classMerge([
        styles.row,
        leavingRow ? styles.rowLeaving : undefined,
        previewRowHeight !== undefined ? styles.rowResizing : undefined,
      ])}
      style={{
        height: displayHeight,
        transition:
          previewRowHeight === undefined && (state === "opening" || state === "closing")
            ? `height ${SCAFFOLD_ANIMATION_DURATION_MS}ms linear`
            : undefined,
      }}
    >
      <ScaffoldRowResizeHandle rowId={rowId} />
      {scaffoldContext.settings.onSelectionChange !== undefined && (
        <ScaffoldRowSelectHandle rowId={rowId} />
      )}
      <div
        className={classMerge([
          styles.rowCells,
          rowHasRowSpanningCells ? styles.rowCellsWithSpan : undefined,
        ])}
      >
        {allDisplayCols.map((c) => (
          <ScaffoldCell
            key={`${rowId}-${c.id}`}
            rowId={rowId}
            colId={c.id}
            rowHeightOverride={previewRowHeight}
          />
        ))}
      </div>
    </div>
  );
};

ScaffoldRow.displayName = "ScaffoldRow";
